import React from "react";
import FocusLock from "react-focus-lock";

import cx from "classnames";

import { getClosureFor } from "@kazoohr/helpers";

import { onBrowser } from "../../helpers/onBrowser";
import { TabIndexVisibility } from "../TabIndexVisibility";

import { CloseDrawerButton } from "./CloseDrawerButton";
import styles from "./Drawer.scss";
import { DrawerHeader } from "./DrawerHeader";

export interface DrawerProps {
  /**
   * React content you want rendered in the drawer action area (in the footer of the drawer).
   */
  action?: boolean | JSX.Element | string;
  /**
   * For screen readers, describe in a one short label what the drawer content is. E.g., "User Profile Form"
   */
  ariaLabel?: string;
  /**
   * If showing the window close button and you want text, pass it in here. Note: you must handle i18n yourself.
   */
  closeButtonText?: string;
  /**
   * Add a custom class to the `drawerContent` div element.
   */
  contentClass?: string;
  /**
   * Allow users to dismiss the drawer by clicking outside of it. Default: true. Not available for inline drawers.
   */
  dismissOnClickOff?: boolean;
  /**
   * Add a custom class to the main `drawer` div element.
   */
  drawerClass?: string;
  /**
   * Causes the drawer to be fixed to the right side of the viewport regardless of current position on the webpage.
   */
  fixedPosition?: boolean;
  /**
   * Displays the drawer.
   */
  isOpen?: boolean;
  /**
   * Function to handle what happens when user input (e.g., clicking outside of the drawer or sliding it shut) closes the drawer.
   */
  onSetOpen:
    | ((isOpen?: boolean) => void)
    | ((isOpen?: string) => void)
    | React.Dispatch<React.SetStateAction<boolean | undefined>>
    | React.Dispatch<React.SetStateAction<boolean>>
    | React.Dispatch<React.SetStateAction<string | undefined>>
    | React.Dispatch<React.SetStateAction<string>>;
  /**
   * Shadow the non-drawer part of the rest of the DOM.
   */
  shadow?: boolean;
  /**
   * Give the drawer a header title.
   */
  title?: JSX.Element | string;
  /**
   * The width of the drawer (include units). Default: 396px. We also max the width at 90% of the viewport width for mobile.
   */
  width?: string;
}

/**
 * A drawer that can display any arbitrary content. You must wrap this component
 * in your own component that controls the opened/closed state of the drawer if
 * you want any sort of advanced functionality for the drawer. To auto-focus a
 * specific element when the drawer is opened, see the [FocusLock documentation
 * for autofocus](https://www.npmjs.com/package/react-focus-lock#autofocus).
 *
 * Fully accessible and keyboard navigable.
 *
 * @example
 * ```jsx
 * function ComponentThatNeedsADrawer() {
 *   const [drawerOpen, setDrawerOpen] = React.useState(false);
 *   return (
 *     {
 *       // You need a new stacking context here, which `position: relative`
 *       // creates. This basically means that the actual drawer content will
 *       // anchor itself to the current stacking context. By default, the
 *       // page body is a stacking context, so if you have no other stacking
 *       // context above the drawer, the drawer will anchor itself to the page.
 *
 *       // If you ever want to anchor a drawer to the page, set the `drawerClass`
 *       // prop and give it `position: fixed`. Fixed positioning anchors the
 *       // element to the body's stacking context.
 *     }
 *     <div style={{ position: "relative" }}>
 *       <h1>Hello world</h1>
 *       <button onClick={() => setDrawerOpen(true)}>Open Drawer</button>
 *       <Drawer
 *         ariaLabel="Label what is in this drawer"
 *         isOpen={drawerOpen}
 *         onSetOpen={setDrawerOpen}
 *         title="Drawer Title"
 *         action={
 *           <DrawerFooter
 *             closeText="Cancel"
 *             onClose={() => setDrawerOpen(false)}
 *             onSave={() => console.log('Saving...')}
 *             saveText="Save"
 *           />
 *         }
 *       >The drawer content goes here!</Drawer>
 *   );
 * }
 * ```
 *
 */
export function Drawer({
  action = false,
  ariaLabel,
  children,
  closeButtonText = "",
  contentClass,
  dismissOnClickOff = true,
  drawerClass,
  fixedPosition = false,
  isOpen = false,
  onSetOpen,
  shadow = false,
  title,
  width = "396px",
}: React.PropsWithChildren<DrawerProps>) {
  const [isFullyOpen, setIsFullyOpen] = React.useState(isOpen);
  const [drawerId] = React.useState("kazoo-drawer-" + drawersCount++);
  const drawerRef = React.useRef<DrawerRef>({
    id: drawerId,
    isOpen,
    dismissOnClickOff,
    onSetOpen,
  });

  // Create our global drawer node for this drawer
  React.useEffect(() => {
    attachEscapeListener();
    attachOffClickListener();

    const index = drawers.push(drawerRef);

    // When we unmount, we want to remove the drawer container
    // from the DOM and our a11y helpers.
    return () => {
      drawers.splice(index - 1, 1);
    };
  }, []);

  React.useEffect(() => {
    drawerRef.current = {
      ...drawerRef.current,
      isOpen,
      dismissOnClickOff,
    };
  }, [isOpen, dismissOnClickOff]);

  // We need local state to tell the drawer whether or not to enable our focus
  // lock functionality. We need to do this because most browsers will move the
  // browser's scroll position to an off-screen or our of scroll area element
  // receives focus. If we wait ~100 or 200 MS, we can prevent the jank that
  // results from this behavior.
  React.useEffect(() => {
    if (isOpen) {
      setTimeout(getClosureFor(setIsFullyOpen, true), 100);
    } else {
      setIsFullyOpen(false);
    }
  }, [isOpen]);

  // This useEffect toggles display of the website document body tag's scrollbar.
  // This drawer has a scrollbar for overflowing content, and having that scrollbar
  // and the main pages scrollbar overlapping was a bad look, so we hide it while
  // the drawer is open. The global scss class is defined in _tools.scss

  React.useEffect(() => {
    if (fixedPosition) {
      if (isOpen) {
        document?.documentElement?.classList?.add("bodyScrollbarDisplayNone");
      }
      return () => {
        document?.documentElement?.classList?.remove(
          "bodyScrollbarDisplayNone"
        );
      };
    }
  }, [isOpen]);

  const lockFocus = isFullyOpen && dismissOnClickOff;
  return (
    <div
      aria-label={ariaLabel}
      className={cx(
        styles.drawer,
        {
          [styles.drawerFixedPosition]: fixedPosition,
          [styles.drawerOpen]: isOpen,
          [styles.drawerInline]: true,
          [styles.drawerNoShadow]: !shadow,
        },
        drawerClass
      )}
      style={{ width }}
      data-testid="drawer-base"
    >
      <div className={styles.drawerScrollContainer}>
        <TabIndexVisibility
          visible={isFullyOpen}
          className={styles.tabIndexVisibility}
        >
          <FocusLock
            disabled={!lockFocus}
            returnFocus={true}
            lockProps={{
              style: {
                minHeight: "100%",
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            {title && (
              <div data-testid="drawer-header-title">
                <DrawerHeader
                  title={title}
                  closeButtonText={closeButtonText}
                  onSetOpen={onSetOpen}
                />
              </div>
            )}
            <div className={cx(styles.drawerContentWrapper)}>
              <div className={cx(styles.drawerContent, contentClass)}>
                {!title && (
                  <CloseDrawerButton
                    text={closeButtonText}
                    onClick={getClosureFor(onSetOpen, false)}
                  />
                )}
                {children}
                {action && <div data-testid="drawer-action-bar">{action}</div>}
              </div>
            </div>
          </FocusLock>
        </TabIndexVisibility>
      </div>
    </div>
  );
}

let drawersCount = 0;
const drawers: React.MutableRefObject<DrawerRef>[] = [];

type DrawerRef = {
  id: string;
  isOpen: boolean;
  dismissOnClickOff: boolean;
  onSetOpen: React.Dispatch<React.SetStateAction<any>>;
};

/**
 * @deprecated Exported for testing only
 */
export function getDrawers() {
  return drawers;
}

const inBrowserEnvironment = onBrowser();

// Setup our global key listener to follow a11y guidelines.
// This function closes the top-most drawer that is open.
function closeTopDrawer(event: Pick<KeyboardEvent, "key">) {
  if (event.key !== "Escape") {
    return;
  }
  drawers.forEach((drawer) => {
    if (drawer.current?.isOpen) {
      drawer.current?.onSetOpen(false);
    }
  });
}

// istanbul ignore next - browser-specific code (makes sure we don't run this in node)
function attachEscapeListener() {
  if (!inBrowserEnvironment) {
    return;
  }
  document.removeEventListener("keydown", closeTopDrawer);
  document.addEventListener("keydown", closeTopDrawer);
}

function closeAllDismissibleDrawers() {
  drawers.forEach((drawer) => {
    if (drawer.current?.isOpen && drawer.current?.dismissOnClickOff) {
      drawer.current?.onSetOpen(false);
    }
  });
}

function isDismissibleClick(element: EventTarget | null | undefined) {
  let node = element as ParentNode | null; // eslint-disable-line @typescript-eslint/consistent-type-assertions
  while (node) {
    const className = "className" in node ? String(node.className) : "";
    if (className.includes("drawer")) {
      return false; // Clicking on the drawer? That's inert
    }
    node = node.parentNode;
  }
  return true;
}

/**
 * @deprecated Exported for testing only
 */
export function bodyClickEvent(event?: Pick<MouseEvent, "target">) {
  if (
    isIgnoredBodyClickTarget(event?.target) ||
    !isDismissibleClick(event?.target)
  ) {
    return;
  }
  closeAllDismissibleDrawers();
}

// istanbul ignore next - browser-specific UX code that helps with types; not really worth the trouble of exercising in tests
function isIgnoredBodyClickTarget(target: EventTarget | null | undefined) {
  if (!target) {
    return true;
  }
  return false;
}

// istanbul ignore next - browser-specific code (makes sure we don't run this in node)
function attachOffClickListener() {
  if (!inBrowserEnvironment) {
    return;
  }

  document.removeEventListener("click", bodyClickEvent, { capture: true });
  document.addEventListener("click", bodyClickEvent, { capture: true });

  document.removeEventListener("tap", bodyClickEvent);
  document.addEventListener("tap", bodyClickEvent);
}
